import { useUiTranslator } from "../../hooks/useUiTranslator";
import { useOnboardingStatus } from "../../onboarding/useOnboardingStatus";
import {
  useGetApiEasyAdministrationArea,
  useGetApiLineItemApiLineItemProperties,
} from "../../openapi/backendApi";
import { useEasySession } from "../../providers/easySessionProvider";
import CopyToClipboard from "../CopyToClipboard/CopyToClipboard";
import { ErrorType } from "../CustomCartIdRouteComponent/CustomCartIdRouteComponent";
import {
  cartStore,
  updateSelectedItems,
} from "../CustomCartIdRouteComponent/storeCart";

import { useCallback, useEffect, useState } from "react";
import { JSX } from "react";

//import { isLineItem, isSubItem } from "../Table/helper/helper";

import {
  IconButtonLink,
  StandardComponents,
  VMItemGroups,
  VMLineItems,
  VMQuotes,
} from "@4pace-cpq/core";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Checkbox,
  TableCell,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSearch } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";

import { theme as CustomLineItemTheme } from "./CustomLineItemTheme";
import { LineItemPropertyField } from "./LineItemPropertyField";

export interface ISyncSelect {
  configId: string;
  checked: boolean;
}
interface ICustomLineItem {
  item: VMLineItems;
  quoteId: VMQuotes["id"];
  itemGroupId: VMItemGroups["id"];
  idx: number;
  error?: ErrorType;
  errorList?: ErrorType[];
}

export function CustomLineItem({
  item,
  quoteId,
  itemGroupId,
  idx,
  error,
  errorList,
}: ICustomLineItem): JSX.Element {
  const t = useUiTranslator();
  const { isLoggedIntoEasy } = useEasySession();
  const { data: lineItemProperties } = useGetApiLineItemApiLineItemProperties({
    lineItemId: item.id,
  });
  const savedNotes = lineItemProperties?.data.notes ?? "";
  const savedCustomerId = lineItemProperties?.data.customerId ?? "";
  const [notes, setNotes] = useState<string>(savedNotes);
  const [customerId, setCustomerId] = useState<string>(savedCustomerId);
  const checked = useStore(cartStore, (state) =>
    state.selectedItems.includes(item.id),
  );
  const { data: administrationAreaData, isFetching } =
    useGetApiEasyAdministrationArea();
  const onboardingStatus = useOnboardingStatus();

  const { productCategory } = useSearch({ strict: false });

  const search = {
    quoteId,
    itemGroupId,
    lineItemId: item.id,
    cfgObjectId: item.product?.cfgObjectIdentifier,
    configurationId: item.configurationId,
    productCategory,
    administrationArea: administrationAreaData?.data.string,
    notes,
    customerId,
    systemEnvironment: import.meta.env.MODE,
  };

  const handleSyncSelect = useCallback((): void => {
    updateSelectedItems(item.id);
  }, [item.id]);

  // Synced per field, so a refetch after saving one field doesn't overwrite
  // an unsaved draft in the other.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNotes(savedNotes);
  }, [savedNotes]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCustomerId(savedCustomerId);
  }, [savedCustomerId]);

  return (
    <ThemeProvider theme={CustomLineItemTheme}>
      {errorList && errorList.length > 0 && (
        <TableCell component="th" scope="row" align="right">
          {error?.exceptionMessage && error?.exceptionMessage !== "" && (
            <Tooltip title={error?.exceptionMessage}>
              <WarningIcon color="warning" />
            </Tooltip>
          )}
        </TableCell>
      )}
      {isLoggedIntoEasy && (
        <TableCell component="th" scope="row" align="right">
          <Checkbox
            checked={checked}
            onChange={handleSyncSelect}
            slotProps={{
              input: {
                "aria-label": t(
                  "CustomApp:cart.table.header.syncSelect",
                  "Sync",
                ),
              },
            }}
          />
        </TableCell>
      )}
      <TableCell component="th" scope="row" align="center">
        {idx + 1}
      </TableCell>
      <TableCell align="left">
        <Box display="flex" alignItems="center">
          <Tooltip title={item.id}>
            <Typography variant="body2" noWrap maxWidth={"4em"}>
              {item.id}
            </Typography>
          </Tooltip>
          <CopyToClipboard data={item.id?.toString()} />
        </Box>
      </TableCell>
      <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
        {lineItemProperties?.data.typeCode}
      </TableCell>
      <TableCell
        align="center"
        sx={{ ">.MuiFormControl-root": { width: "4rem" } }}
      >
        <StandardComponents.ConnectedQuantity
          item={item}
          value={item.quantity || 1}
          allowDecimal={false}
        />
      </TableCell>
      <TableCell align="left">
        <LineItemPropertyField
          lineItemId={item.id}
          field="notes"
          value={notes}
          savedValue={savedNotes}
          onChange={setNotes}
          ariaLabel={t("CustomApp:cart.table.header.notes", "Notes")}
        />
      </TableCell>
      <TableCell align="left">
        <LineItemPropertyField
          lineItemId={item.id}
          field="customerId"
          value={customerId}
          savedValue={savedCustomerId}
          onChange={setCustomerId}
          ariaLabel={t("CustomApp:cart.table.header.customerId", "ID")}
          sx={{ maxWidth: "6rem" }}
        />
      </TableCell>
      {/*    <TableCell align="left">
        <PriceIndicator
          price={Number(lineItemProperties?.data.priceIndicator)}
        />
      </TableCell> */}
      {/* {isLoggedIntoEasy && <TableCell align="left">Completed</TableCell>} */}
      <TableCell align="right">
        <Box display="flex">
          <Tooltip title={t("CustomApp:buttons.edit", "Edit")}>
            <IconButtonLink
              to="/configurator"
              search={search}
              disabled={isFetching || onboardingStatus !== "ready"}
            >
              <EditIcon
                color={item.configuration?.isComplete ? "success" : "warning"}
              />
            </IconButtonLink>
          </Tooltip>
          <StandardComponents.CloneItemIconButton item={item} />
          <StandardComponents.DeleteItemIconButton item={item} />
        </Box>
      </TableCell>
    </ThemeProvider>
  );
}
